import { useAppNavigate } from '@/hooks/use-app-navigate';
import { gameExit } from '@/lib/game-exit.ts';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { urlParams } from '@/app/urlParams';

import { GameFailureScreen, GameSuccessScreen } from '@/section/results/game-result-screen';
import { useDemoStore } from '@/store/demo';
import { useLiveStore } from '@/store/live';
import { STAGE_THEME_COLORS } from '@/theme/stage-colors';
import type { StageId } from '@/types/stage-theme';
import { getConfiguredUiStageId, normalizeStageId } from '@/utils/stage/stage-utils';
import { paths } from '@app/router/routes';

export default function ResultPage() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { result, clear } = useLiveStore(useShallow((s) => ({ result: s.result, clear: s.clear })));
  const goToStart = useDemoStore((s) => s.goToStart);
  const clearLiveSession = useLiveStore((s) => s.clearLiveSession);
  const hadResultRef = useRef(false);
  const [isContinuePending, setIsContinuePending] = useState(false);

  useEffect(() => {
    if (result) {
      hadResultRef.current = true;
      return;
    }
    if (!hadResultRef.current) {
      navigate(paths.home, { replace: true });
    }
  }, [result, navigate]);

  if (!result) {
    const stage = getConfiguredUiStageId();
    const theme = STAGE_THEME_COLORS[stage];
    return (
      <motion.div
        className="resultViewport"
        style={{ backgroundColor: theme.background }}
        aria-busy="true"
        aria-label="Loading results"
      />
    );
  }

  const stage: StageId = normalizeStageId(result.stage);
  const score = Number.isFinite(result.score) ? Math.max(0, result.score) : 0;
  const completedGames = Number.isFinite(result.completed) ? Math.max(0, result.completed) : 0;
  const totalGames = Number.isFinite(result.total)
    ? Math.max(1, result.total, completedGames)
    : Math.max(1, completedGames);

  const handleContinue = (nextStage: StageId) => {

    if (isContinuePending) {
      return;
    }
    void (async () => {
      setIsContinuePending(true);
      try {
        clear();
        clearLiveSession();
        const isZitadelMode = !!urlParams.token;
        if (isZitadelMode) {
          await gameExit(
            {
              closeTab: t('results.exitCloseTabHint'),
              appBack: t('results.exitUseAppBackHint'),
            },
            (message) => {
              toast.info(message);
            },
            { exitDelayMs: 0 },
          );
        } else {
          await goToStart(nextStage);
          navigate(paths.home, { replace: true });
        }
      } finally {
        setIsContinuePending(false);
      }
    })();
  };

  const continueProps = {
    isContinueDisabled: isContinuePending,
  };

  if (score === 0) {
    return (
      <GameFailureScreen
        stage={stage}
        score={score}
        completedGames={completedGames}
        totalGames={totalGames}
        onContinue={handleContinue}
        {...continueProps}
      />
    );
  }

  return (
    <GameSuccessScreen
      stage={stage}
      score={score}
      completedGames={completedGames}
      totalGames={totalGames}
      onContinue={handleContinue}
      {...continueProps}
    />
  );
}
